import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { RefreshCw } from "lucide-react";
import { useTranslation } from "react-i18next";
import { formatDateSr } from "@normax-performance/shared-format";
import { analyticsApi } from "../../lib/api-client/endpoints";
import { Button } from "../../shared-ui/primitives";
import { PeriodControl } from "./components/PeriodControl";
import { PerformanceOverview } from "./components/PerformanceOverview";
import { ZoneDistribution } from "./components/ZoneDistribution";
import { AnalyticsTabs, type AnalyticsView } from "./components/AnalyticsTabs";
import { WorkersTable } from "./components/WorkersTable";
import { ProductsTable } from "./components/ProductsTable";
import { OperationsTable } from "./components/OperationsTable";
import { ActivitiesView } from "./components/ActivitiesView";
import { AnalyticsEmptyState } from "./components/AnalyticsEmptyState";
import { computeDefaultAnalyticsPeriod, computeQuickPeriod, type AnalyticsPeriodRange, type QuickPeriodKey } from "./period";
import "./AnalyticsPage.css";

const QUICK_KEYS: QuickPeriodKey[] = ["yesterday", "thisWeek", "thisMonth", "previousMonth"];

function detectActiveQuickKey(period: AnalyticsPeriodRange): QuickPeriodKey | null {
  const today = new Date();
  for (const key of QUICK_KEYS) {
    const candidate = computeQuickPeriod(key, today);
    if (candidate.from === period.from && candidate.to === period.to) return key;
  }
  return null;
}

function TableSkeleton() {
  return (
    <div className="nx-analytics-table-wrap">
      <table className="nx-analytics-table nx-table">
        <tbody>
          {[0, 1, 2, 3, 4].map((i) => (
            <tr key={i}>
              <td colSpan={7}>
                <span className="nx-skeleton" style={{ width: "100%", height: "18px" }} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SectionError({ onRetry }: { onRetry: () => void }) {
  const { t } = useTranslation("analytics");
  return (
    <div className="nx-analytics__error">
      <p className="nx-analytics__error-title">{t("error.title")}</p>
      <p>{t("error.description")}</p>
      <Button variant="secondary" onClick={onRetry}>
        <RefreshCw size={16} />
        {t("error.retry")}
      </Button>
    </div>
  );
}

/**
 * NormaX Performance Intelligence — Analytics (Slice B, docs/
 * NORMAX_PERFORMANCE_ANALYTICS_FRONTEND_SLICE_B_IMPLEMENTATION_REPORT.md).
 * Replaces the /analitika ComingSoonPage placeholder.
 *
 * Data-fetch strategy (this slice's spec §31): `summary` is always fetched
 * (it drives the hero/chips/zone-distribution, visible regardless of which
 * tab is active); the active view's own endpoint is fetched in addition —
 * `enabled: view === "..."` means switching tabs never blindly fetches all
 * 5 endpoints, and react-query's own cache means switching back to an
 * already-visited tab within the same period does not re-fetch.
 */
export function AnalyticsPage() {
  const { t } = useTranslation("analytics");
  const [period, setPeriod] = useState<AnalyticsPeriodRange>(() => computeDefaultAnalyticsPeriod(new Date()));
  const [view, setView] = useState<AnalyticsView>("workers");

  const activeQuickKey = useMemo(() => detectActiveQuickKey(period), [period]);

  const summaryQuery = useQuery({
    queryKey: ["analytics", "summary", period.from, period.to],
    queryFn: () => analyticsApi.summary(period.from, period.to),
  });
  const workersQuery = useQuery({
    queryKey: ["analytics", "workers", period.from, period.to],
    queryFn: () => analyticsApi.workers(period.from, period.to),
    enabled: view === "workers",
  });
  const productsQuery = useQuery({
    queryKey: ["analytics", "products", period.from, period.to],
    queryFn: () => analyticsApi.products(period.from, period.to),
    enabled: view === "products",
  });
  const operationsQuery = useQuery({
    queryKey: ["analytics", "operations", period.from, period.to],
    queryFn: () => analyticsApi.operations(period.from, period.to),
    enabled: view === "operations",
  });
  const activityQuery = useQuery({
    queryKey: ["analytics", "activity-breakdown", period.from, period.to],
    queryFn: () => analyticsApi.activityBreakdown(period.from, period.to),
    enabled: view === "activities",
  });

  const isEmptyPeriod = summaryQuery.data ? summaryQuery.data.completeness.entryCount === 0 : false;

  function renderActiveView() {
    switch (view) {
      case "workers":
        if (workersQuery.isError) return <SectionError onRetry={() => workersQuery.refetch()} />;
        if (workersQuery.isLoading || !workersQuery.data) return <TableSkeleton />;
        return <WorkersTable response={workersQuery.data} />;
      case "products":
        if (productsQuery.isError) return <SectionError onRetry={() => productsQuery.refetch()} />;
        if (productsQuery.isLoading || !productsQuery.data) return <TableSkeleton />;
        return <ProductsTable response={productsQuery.data} />;
      case "operations":
        if (operationsQuery.isError) return <SectionError onRetry={() => operationsQuery.refetch()} />;
        if (operationsQuery.isLoading || !operationsQuery.data) return <TableSkeleton />;
        return <OperationsTable response={operationsQuery.data} />;
      case "activities":
        if (activityQuery.isError) return <SectionError onRetry={() => activityQuery.refetch()} />;
        if (activityQuery.isLoading || !activityQuery.data) return <TableSkeleton />;
        return <ActivitiesView data={activityQuery.data} />;
    }
  }

  return (
    <div className="nx-analytics">
      <div className="nx-analytics__header">
        <div>
          <h1 className="nx-analytics__title">{t("title")}</h1>
          <p className="nx-analytics__subtitle">{t("subtitle")}</p>
        </div>
        <PeriodControl period={period} onChange={setPeriod} activeQuickKey={activeQuickKey} />
      </div>

      {summaryQuery.isError ? (
        <SectionError onRetry={() => summaryQuery.refetch()} />
      ) : isEmptyPeriod ? (
        <AnalyticsEmptyState period={period} onChangePeriod={setPeriod} />
      ) : (
        <>
          <PerformanceOverview summary={summaryQuery.data} isLoading={summaryQuery.isLoading} />
          <ZoneDistribution summary={summaryQuery.data} isLoading={summaryQuery.isLoading} />

          {/* Sticky context (task: sticky context + table headers, revised —
              docs/NORMAX_PERFORMANCE_REPORTS_ANALYTICS_SCROLL_STICKY_FORENSIC_REPORT.md):
              a compact recap of the page title + selected period, plus the
              active-tab context, stays visible while a long table scrolls —
              one page-level sticky layer, resolved against the app's real
              page scroll (.nx-shell__content). Each table's own header row
              sticks against that SAME scroll (no bounded region of its own
              any more), offset below this bar via `--sticky-context-height`
              (set on `.nx-analytics` in AnalyticsPage.css). */}
          <div className="nx-sticky-subnav">
            <div className="nx-analytics__sticky-recap">
              <span className="nx-analytics__sticky-title">{t("title")}</span>
              <span className="nx-analytics__sticky-period">
                {formatDateSr(period.from)} – {formatDateSr(period.to)}
              </span>
            </div>
            <AnalyticsTabs active={view} onChange={setView} />
          </div>
          <div role="tabpanel" id={`analytics-panel-${view}`} aria-labelledby={`analytics-tab-${view}`}>
            {renderActiveView()}
          </div>
        </>
      )}
    </div>
  );
}
